import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ImagePlus, X, List, ListOrdered, Smile } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  ManagerNote,
  NoteInput,
  NOTE_BODY_LIMIT,
  NOTE_TITLE_LIMIT,
  NotePriority,
  NoteChip,
  GameweekMeta,
} from "@/hooks/useManagerNotes";
import NoteImage from "./NoteImage";

interface Props {
  gameweeks: GameweekMeta[];
  defaultGameweek: number;
  existing?: ManagerNote | null;
  takenGameweeks: number[];
  onCancel: () => void;
  onSave: (input: NoteInput) => Promise<void>;
  onUploadImage: (file: File) => Promise<string>;
}

const EMOJIS = ["⚽", "🔥", "🎯", "⚠️", "✅", "💰", "🧠", "📈", "🩹", "🅲"];

const NoteEditor = ({
  gameweeks,
  defaultGameweek,
  existing,
  takenGameweeks,
  onCancel,
  onSave,
  onUploadImage,
}: Props) => {
  const { toast } = useToast();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [gameweek, setGameweek] = useState<number>(existing?.gameweek ?? defaultGameweek);
  const [title, setTitle] = useState(existing?.title ?? "");
  const [body, setBody] = useState(existing?.body ?? "");
  const [chip, setChip] = useState<NoteChip>((existing?.chip_plan as NoteChip) ?? "none");
  const [captain, setCaptain] = useState(existing?.captain ?? "");
  const [transfersIn, setTransfersIn] = useState(existing?.transfers_in ?? "");
  const [transfersOut, setTransfersOut] = useState(existing?.transfers_out ?? "");
  const [priority, setPriority] = useState<NotePriority>(existing?.priority ?? "normal");
  const [reminder, setReminder] = useState(existing?.reminder ?? false);
  const [imageUrl, setImageUrl] = useState<string | null>(existing?.image_url ?? null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!existing) setGameweek(defaultGameweek);
  }, [defaultGameweek, existing]);

  const insertAtCursor = (text: string) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => (b + text).slice(0, NOTE_BODY_LIMIT));
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = (body.slice(0, start) + text + body.slice(end)).slice(0, NOTE_BODY_LIMIT);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = Math.min(start + text.length, NOTE_BODY_LIMIT);
      el.setSelectionRange(pos, pos);
    });
  };

  const addBullets = (ordered: boolean) => {
    const lines = body ? body.split("\n") : [];
    const prefix = ordered ? `${lines.filter((l) => /^\d+\./.test(l)).length + 1}. ` : "• ";
    insertAtCursor((body && !body.endsWith("\n") ? "\n" : "") + prefix);
  };

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      setImageUrl(url);
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        gameweek,
        title: title.trim() || null,
        body,
        chip_plan: chip,
        captain: captain.trim() || null,
        transfers_in: transfersIn.trim() || null,
        transfers_out: transfersOut.trim() || null,
        priority,
        image_url: imageUrl,
        reminder,
      });
      toast({ title: existing ? "Note updated" : "Note saved" });
      onCancel();
    } catch (e: any) {
      toast({ title: "Could not save note", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const gwOptions = gameweeks.length
    ? gameweeks
    : Array.from({ length: 38 }, (_, i) => ({
        id: i + 1,
        name: `Gameweek ${i + 1}`,
        deadline_time: null,
        finished: false,
        is_current: false,
        is_next: false,
      }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Gameweek</Label>
          <Select
            value={String(gameweek)}
            onValueChange={(v) => setGameweek(Number(v))}
            disabled={!!existing}
          >
            <SelectTrigger className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {gwOptions.map((g) => (
                <SelectItem
                  key={g.id}
                  value={String(g.id)}
                  disabled={!existing && takenGameweeks.includes(g.id)}
                  className="text-xs"
                >
                  {g.name}
                  {g.is_current ? " • current" : g.finished ? " • past" : ""}
                  {!existing && takenGameweeks.includes(g.id) ? " (note exists)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Priority</Label>
          <Select value={priority} onValueChange={(v) => setPriority(v as NotePriority)}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low" className="text-xs">Low</SelectItem>
              <SelectItem value="normal" className="text-xs">Normal</SelectItem>
              <SelectItem value="key" className="text-xs">Key gameweek</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Title</Label>
          <span className="text-[10px] text-muted-foreground">
            {title.length}/{NOTE_TITLE_LIMIT}
          </span>
        </div>
        <Input
          value={title}
          maxLength={NOTE_TITLE_LIMIT}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Double gameweek plan"
          className="h-9 text-xs"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Notes</Label>
          <span className="text-[10px] text-muted-foreground">
            {body.length}/{NOTE_BODY_LIMIT}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => addBullets(false)}>
            <List className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => addBullets(true)}>
            <ListOrdered className="w-3.5 h-3.5" />
          </Button>
          <Smile className="w-3.5 h-3.5 text-muted-foreground ml-1" />
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => insertAtCursor(e)}
              className="text-sm leading-none px-1 py-0.5 rounded hover:bg-muted"
              aria-label={`Insert ${e}`}
            >
              {e}
            </button>
          ))}
        </div>
        <Textarea
          ref={bodyRef}
          value={body}
          maxLength={NOTE_BODY_LIMIT}
          onChange={(e) => setBody(e.target.value)}
          placeholder={"• Captain shortlist\n• Transfers to consider\n• Fixtures to target"}
          className="min-h-[130px] text-xs"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Chip plan</Label>
          <Select value={chip} onValueChange={(v) => setChip(v as NoteChip)}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">No chip</SelectItem>
              <SelectItem value="wildcard" className="text-xs">Wildcard</SelectItem>
              <SelectItem value="freehit" className="text-xs">Free Hit</SelectItem>
              <SelectItem value="bboost" className="text-xs">Bench Boost</SelectItem>
              <SelectItem value="3xc" className="text-xs">Triple Captain</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Captain pick</Label>
          <Input
            value={captain}
            maxLength={80}
            onChange={(e) => setCaptain(e.target.value)}
            placeholder="e.g. Haaland"
            className="h-9 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Transfers in</Label>
          <Input
            value={transfersIn}
            maxLength={160}
            onChange={(e) => setTransfersIn(e.target.value)}
            placeholder="Player A, Player B"
            className="h-9 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Transfers out</Label>
          <Input
            value={transfersOut}
            maxLength={160}
            onChange={(e) => setTransfersOut(e.target.value)}
            placeholder="Player C"
            className="h-9 text-xs"
          />
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
        <div>
          <p className="text-xs font-medium">Deadline reminder</p>
          <p className="text-[10px] text-muted-foreground">Highlight this note before the deadline</p>
        </div>
        <Switch checked={reminder} onCheckedChange={setReminder} />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Image (optional, max 2MB)</Label>
        {imageUrl ? (
          <div className="relative w-fit">
            <NoteImage path={imageUrl} className="h-20 rounded-md object-cover" />
            <button
              type="button"
              onClick={() => setImageUrl(null)}
              className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground p-1"
              aria-label="Remove image"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}
            Upload image
          </Button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button size="sm" className="flex-1 text-xs" onClick={submit} disabled={saving}>
          {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
          {existing ? "Update note" : "Save note"}
        </Button>
        <Button size="sm" variant="outline" className="text-xs" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default NoteEditor;
